/**
 * StaffSignupPage — A-4. 일반 회원가입 (소속 직원)
 * Step 1: 사업장 검색 (상호명 키워드 → 목록 선택 → compId 확정)
 * Step 2: 계정 정보  (가입자명, 아이디+중복확인, 비밀번호+강도, 비밀번호확인, 전화번호+OTP 인증, 이메일, 약관) → POST /member/join
 *
 * 폼 컨트롤은 전부 ui-core 표준 컴포넌트/클래스다 (D-09 §15.1 버튼 · §15.2 입력 폼).
 * 인라인 style + JS hover/focus 재구현은 2026-09-07 에 걷어냈다 — 색·높이·포커스 링은 토큰이 구동한다.
 */
import { useState, useRef, useCallback, useId } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@indipia/shared';
import { Button, Input } from '@indipia/ui-core';
import { checkUserExists, checkEmailExists, requestPhoneVerify, verifyPhoneCode } from '../api/member-auth';
import { checkLoginId, searchBiz, joinNormal, BizSearchItem } from '../api/member-join';
import { loginWithIdPassword } from '../api/auth';
import { applyLoginTokens } from '../lib/post-login';
import { TermsConsentSection, INITIAL_TERMS_CONSENT, type TermsConsentValue } from '../components/TermsConsentSection';
import { PhoneOtpVerify, PhoneOtpSendButton } from '../components/PhoneOtpVerify';
import { PasswordField } from '../components/PasswordField';

// ── 비밀번호 강도 ─────────────────────────────────────────────────────────

function pwStrength(pw: string): 0 | 1 | 2 | 3 {
  if (!pw) return 0;
  const hasLetter  = /[a-zA-Z]/.test(pw);
  const hasNumber  = /\d/.test(pw);
  const hasSpecial = /[!@#$%^&*()_+\-=[\]{}|;':",.<>?/]/.test(pw);
  const long       = pw.length >= 8;
  const cats       = [hasLetter, hasNumber, hasSpecial].filter(Boolean).length;
  if (!long || cats < 2) return 1;
  if (cats === 2)        return 2;
  return 3;
}

const PW_LABEL  = ['', '약함', '보통', '강함'];
const PW_COLORS = ['var(--shell-border)', 'var(--color-error)', 'var(--color-warning)', 'var(--color-success)'];

/** 비밀번호 강도 바 — PasswordField 의 footer 슬롯에 얹는다 */
function StrengthBar({ password, strength }: { password: string; strength: 0 | 1 | 2 | 3 }) {
  return (
    <>
      <div style={{ display: 'flex', gap: 5, marginTop: 8 }}>
        {[1, 2, 3].map((n) => (
          <div key={n} style={{ flex: 1, height: 3, borderRadius: 2, background: password && strength >= n ? PW_COLORS[strength] : 'var(--shell-border)', transition: 'background .2s' }} />
        ))}
      </div>
      {password && <p className="text-caption" style={{ marginTop: 5, color: PW_COLORS[strength] }}>강도: {PW_LABEL[strength]}</p>}
    </>
  );
}

/** 인디고 브랜드 면(shell.point) 위 흰 계열 — hex·rgba 없이 토큰으로 표현 */
const onPoint = (pct: number) => `color-mix(in srgb, var(--gt-on-primary) ${pct}%, transparent)`;

// ── 사업자등록번호 표시 포맷 (000-00-00000) ────────────────────────────────

function formatCompNo(v: string): string {
  const d = v.replace(/\D/g, '').slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 6) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
}

// ── Stepper ───────────────────────────────────────────────────────────────

type StepState = 'done' | 'active' | 'upcoming';

function Stepper({ steps, current }: { steps: { label: string; desc: string }[]; current: number }) {
  const getState = (i: number): StepState => {
    if (i + 1 < current) return 'done';
    if (i + 1 === current) return 'active';
    return 'upcoming';
  };

  const iconColor: Record<StepState, React.CSSProperties> = {
    done:     { background: 'var(--alpha-white-200)', border: `1.5px solid ${onPoint(35)}`, color: 'var(--gt-on-primary)' },
    active:   { background: 'var(--shell-surface)', border: '1.5px solid var(--shell-surface)', color: 'var(--shell-point)', boxShadow: `0 0 0 4px ${'var(--alpha-white-200)'}` },
    upcoming: { background: 'transparent', border: `1.5px solid ${onPoint(35)}`, color: 'var(--alpha-white-800)' },
  };
  const numColor:   Record<StepState, string> = { done: 'var(--alpha-white-800)', active: 'var(--gt-on-primary)', upcoming: 'var(--alpha-white-800)' };
  const labelColor: Record<StepState, string> = { done: 'var(--alpha-white-800)', active: 'var(--gt-on-primary)', upcoming: 'var(--alpha-white-800)' };
  const descColor:  Record<StepState, string> = { done: 'var(--alpha-white-800)', active: 'var(--alpha-white-800)', upcoming: 'var(--alpha-white-800)' };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
      {steps.map((s, i) => {
        const st = getState(i);
        return (
          <div key={i} style={{ display: 'flex', gap: 16, alignItems: 'flex-start', position: 'relative', paddingBottom: i < steps.length - 1 ? 36 : 0 }}>
            {i < steps.length - 1 && (
              <div style={{ position: 'absolute', left: 15, top: 38, width: 1, height: 40, background: onPoint(35) }} />
            )}
            <div className="text-body-sm font-bold" style={{ width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2, ...iconColor[st] }}>
              {st === 'done' ? '✓' : i + 1}
            </div>
            <div>
              <div className="text-caption font-bold uppercase tracking-widest" style={{ marginBottom: 3, color: numColor[st] }}>
                Step 0{i + 1}
              </div>
              <div className="text-heading-sm font-bold" style={{ color: labelColor[st] }}>{s.label}</div>
              <div className="text-caption" style={{ marginTop: 3, color: descColor[st] }}>{s.desc}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}


// ── 메인 컴포넌트 ─────────────────────────────────────────────────────────

const STAFF_STEPS = [
  { label: '사업장 검색',  desc: '소속 사업장 확인' },
  { label: '계정 정보',   desc: '아이디 · 비밀번호 · 연락처 설정' },
];

export function StaffSignupPage() {
  const navigate    = useNavigate();
  const queryClient = useQueryClient();
  const setAuth     = useAuthStore((s) => s.setAuth);

  const [step, setStep] = useState<1 | 2>(1);

  const loginIdId = useId();
  const mobileId  = useId();

  // ── Step 1 상태 ──────────────────────────────────────────────────────────
  const [keyword,      setKeyword]      = useState('');
  const [searchResult, setSearchResult] = useState<BizSearchItem[]>([]);
  const [searching,    setSearching]    = useState(false);
  const [selectedBiz,  setSelectedBiz]  = useState<BizSearchItem | null>(null);
  const [noResult,     setNoResult]     = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleKeywordChange = useCallback((val: string) => {
    setKeyword(val);
    setSelectedBiz(null);
    setNoResult(false);
    setStep1Err(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.trim().length < 2) { setSearchResult([]); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const list = await searchBiz(val.trim());
        setSearchResult(list);
        setNoResult(list.length === 0);
      } catch { setSearchResult([]); }
      finally { setSearching(false); }
    }, 600);
  }, []);

  const handleSelectBiz = (item: BizSearchItem) => {
    setSelectedBiz(item);
    setSearchResult([]);
    setKeyword(item.compName);
    setStep1Err(null);
  };

  const [step1Err, setStep1Err] = useState<string | null>(null);

  const validateStep1 = (): boolean => {
    if (!selectedBiz) { setStep1Err('소속 사업장을 검색해 선택해 주세요.'); return false; }
    setStep1Err(null);
    return true;
  };

  // ── Step 2 상태 ──────────────────────────────────────────────────────────
  const [memberName,   setMemberName]   = useState('');
  const [loginId,      setLoginId]      = useState('');
  const [loginIdState, setLoginIdState] = useState<'idle' | 'checking' | 'ok' | 'taken'>('idle');
  const [loginIdFormatErr, setLoginIdFormatErr] = useState<string | null>(null);
  const lastCheckedId  = useRef('');
  const [password,     setPassword]     = useState('');
  const [pwConfirm,    setPwConfirm]    = useState('');

  // ── Step 2 검증 상태 ─────────────────────────────────────────────────────
  const [memberNameTouched, setMemberNameTouched] = useState(false);
  const [pwTouched,         setPwTouched]         = useState(false);
  const [pwConfirmTouched,  setPwConfirmTouched]  = useState(false);
  const memberNameErr = memberNameTouched && !memberName.trim() ? '가입자명을 입력해 주세요.' : null;

  const handleCheckLoginId = async () => {
    const id = loginId.trim();
    if (id.length === 0) { setLoginIdFormatErr('아이디를 입력해 주세요.'); return; }
    if (id.length < 6)   { setLoginIdFormatErr('6자 이상 입력해 주세요.'); return; }
    setLoginIdFormatErr(null);
    if (id === lastCheckedId.current && loginIdState !== 'idle') return;
    setLoginIdState('checking');
    try {
      const available = await checkLoginId(id);
      lastCheckedId.current = id;
      setLoginIdState(available ? 'ok' : 'taken');
    } catch { setLoginIdState('idle'); }
  };

  const strength = pwStrength(password);
  const pwValid  = strength >= 2 && /[a-zA-Z]/.test(password) && /\d/.test(password) && /[!@#$%^&*()_+\-=[\]{}|;':",.<>?/]/.test(password) && password.length >= 8;
  const pwErr = pwTouched && !password ? '비밀번호를 입력해 주세요.'
    : pwTouched && !pwValid ? '8자 이상, 영문 + 숫자 + 특수문자를 모두 포함해 주세요.'
    : null;
  const pwConfirmErr = pwConfirmTouched && !pwConfirm ? '비밀번호 확인을 입력해 주세요.'
    : pwConfirm && pwConfirm !== password ? '비밀번호가 일치하지 않아요.'
    : null;
  const pwConfirmOk = !!pwConfirm && pwConfirm === password;

  // ── 연락처·이메일·약관 상태 (Step 2에 통합) ─────────────────────────────
  const [mobile,        setMobile]        = useState('');
  const [mobileAccountType, setMobileAccountType] = useState<'general' | 'sns' | null>(null);
  const [mobileTouched, setMobileTouched] = useState(false);
  const [otpTouched,    setOtpTouched]    = useState(false);
  const [email,         setEmail]         = useState('');
  const [emailErr,      setEmailErr]      = useState<string | null>(null);
  const [terms,         setTerms]         = useState<TermsConsentValue>(INITIAL_TERMS_CONSENT);
  const [termsTouched,  setTermsTouched]  = useState(false);
  const [isLoading,     setIsLoading]     = useState(false);
  const [submitError,   setSubmitError]   = useState<string | null>(null);

  // 전화번호 본인 확인 OTP — 모든 가입자 필수
  const [otpSent,     setOtpSent]     = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  // 인증 완료 토큰 — 가입 요청에 실어 보낸다(같은 번호의 퇴사·거절 직원 계정을 닫을 때 서버가 확인)
  const [verifyToken, setVerifyToken] = useState<string | null>(null);
  const [otpLoading,  setOtpLoading]  = useState(false);
  const [otpError,    setOtpError]    = useState<string | null>(null);

  const formatPhone = (v: string) => {
    const d = v.replace(/\D/g, '').slice(0, 11);
    if (d.length < 4) return d;
    if (d.length < 8) return `${d.slice(0,3)}-${d.slice(3)}`;
    return `${d.slice(0,3)}-${d.slice(3,7)}-${d.slice(7)}`;
  };

  const handleMobileChange = (v: string) => {
    setMobile(formatPhone(v));
    setMobileAccountType(null);
    setOtpSent(false);
    setOtpVerified(false);
    setVerifyToken(null);
    setOtpError(null);
  };

  // 전화번호 가입 여부 확인 — 결과를 상태에 반영하고 돌려준다(blur·인증번호 발송 전 공용)
  const checkMobileAccount = async (): Promise<'general' | 'sns' | null> => {
    const digits = mobile.replace(/\D/g, '');
    if (digits.length < 10) { setMobileAccountType(null); return null; }
    try {
      const r = await checkUserExists(mobile);
      const type = !r.exists ? null : r.hasPassword ? 'general' : 'sns';
      setMobileAccountType(type);
      return type;
    } catch { setMobileAccountType(null); return null; }
  };

  const handleMobileBlur = async () => {
    setMobileTouched(true);
    await checkMobileAccount();
  };

  const handleOtpRequest = async () => {
    setOtpLoading(true);
    setOtpError(null);
    try {
      // 번호 입력 직후 바로 누르면 blur 중복 확인보다 먼저 실행된다 — 보내기 전에 직접 확인하고, 이미 가입된 번호면 보내지 않는다
      setMobileTouched(true);
      if (await checkMobileAccount() === 'general') return;
      await requestPhoneVerify(mobile);
      setOtpSent(true);
    } catch {
      setOtpError('인증번호 발송에 실패했습니다. 다시 시도해 주세요.');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleOtpVerify = async (code: string) => {
    setOtpLoading(true);
    setOtpError(null);
    try {
      const result = await verifyPhoneCode(mobile, code);
      if (!result.verified) {
        setOtpError('인증번호가 일치하지 않습니다.');
        return;
      }
      setOtpVerified(true);
      setVerifyToken(result.verifyToken);
    } catch {
      setOtpError('인증번호 확인에 실패했습니다. 다시 시도해 주세요.');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleEmailBlur = async () => {
    if (!email.includes('@')) { setEmailErr('이메일 형식이 올바르지 않아요.'); return; }
    try {
      const r = await checkEmailExists(email);
      if (r.exists) setEmailErr('이미 가입된 이메일이에요.');
      else           setEmailErr(null);
    } catch { setEmailErr(null); }
  };

  const mobileDigits    = mobile.replace(/\D/g, '');
  const mobileIsMobileFormat = mobileDigits.startsWith('010') || mobileDigits.startsWith('999'); // 999=테스트 목업 번호
  const mobileReady     = mobileDigits.length >= 10 && mobileIsMobileFormat;
  const mobileErr = mobileTouched && mobileDigits.length >= 10 && !mobileIsMobileFormat ? '010으로 시작하는 휴대폰 번호를 입력해 주세요.'
    : mobileTouched && mobileDigits.length < 10 ? '전화번호를 정확히 입력해 주세요.'
    : null;
  const otpErr    = otpTouched && mobileReady && !otpVerified ? '전화번호 인증을 완료해 주세요.' : null;
  const termsErr  = termsTouched  && !terms.terms ? '필수 약관에 동의해 주세요.' : null;

  /** 전화번호 필드에 걸리는 오류/안내 — `.field` 상태 클래스와 `.help` 를 함께 결정한다 */
  const mobileNotice = mobileErr
    ?? (mobileAccountType === 'general' ? '이미 가입된 전화번호예요. 로그인해 주세요.' : null)
    ?? otpErr;

  const validateStep2 = (): boolean => {
    setMemberNameTouched(true);
    setPwTouched(true);
    setPwConfirmTouched(true);
    setMobileTouched(true);
    setOtpTouched(true);
    setTermsTouched(true);
    let ok = true;
    if (!memberName.trim()) ok = false;
    if (loginIdState !== 'ok') {
      const id = loginId.trim();
      if (id.length === 0)      setLoginIdFormatErr('아이디를 입력해 주세요.');
      else if (id.length < 6)   setLoginIdFormatErr('6자 이상 입력해 주세요.');
      else                      setLoginIdFormatErr('아이디 중복확인을 완료해 주세요.');
      ok = false;
    }
    if (!password || !pwValid) ok = false;
    if (!pwConfirm || pwConfirm !== password) ok = false;
    if (!mobileReady) ok = false;
    else if (!otpVerified) ok = false;
    if (!email.includes('@')) { setEmailErr('이메일 형식이 올바르지 않아요.'); ok = false; }
    else if (emailErr) ok = false;
    if (!terms.terms) ok = false;
    return ok;
  };

  // ── 최종 제출 ─────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!selectedBiz) return;
    setIsLoading(true); setSubmitError(null);
    try {
      await joinNormal({
        loginId:            loginId.trim(),
        email,
        password,
        memberName:         memberName.trim(),
        mobile,
        verifyToken:        verifyToken ?? undefined,
        master:             false,
        compNo:              selectedBiz.compNo,
        compName:           selectedBiz.compName,
        compRepName:         selectedBiz.compRepName,
        marketingConsent:   terms.marketing,
        marketingChannels:  terms.channels,
        nightMarketingConsent: terms.nightAd,
      });
    } catch (e) {
      const err = e as { response?: { data?: { code?: string; message?: string } } };
      const code = err?.response?.data?.code;
      if (code === 'DUPLICATE_MOBILE') setSubmitError('이미 일반 계정이 있는 전화번호예요. 로그인해 주세요.');
      else if (code === 'DUPLICATE_LOGIN_ID') setSubmitError('이미 사용 중인 아이디예요. 다른 아이디를 입력해 주세요.');
      else setSubmitError(err?.response?.data?.message ?? '가입 처리 중 오류가 발생했습니다.');
      setIsLoading(false);
      return;
    }

    // ── 가입 성공 → 자동로그인 (실패해도 "가입 성공"은 유지, 로그인 화면으로 폴백) ──
    try {
      const tokens = await loginWithIdPassword(loginId.trim(), password);
      applyLoginTokens(tokens, setAuth, queryClient);
      void navigate('/app', { replace: true });
    } catch {
      void navigate('/login', { replace: true, state: { signupDone: true } });
    } finally {
      setIsLoading(false);
    }
  };

  // ── 렌더 ─────────────────────────────────────────────────────────────────
  return (
    <div className="bg-surface" style={{ display: 'flex', minHeight: '100vh' }}>
      {/* 좌측 패널 */}
      <div className="bg-primary-700" style={{ width: '36%', flexShrink: 0, display: 'flex', flexDirection: 'column', padding: '52px 44px 48px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 52 }}>
          <span className="text-body-sm font-bold uppercase tracking-widest text-white">INDIPIA</span>
          <span className="bg-white/60" style={{ width: 5, height: 5, borderRadius: '50%' }} />
          <span className="text-caption font-semibold uppercase tracking-widest text-[var(--alpha-white-800)]">Pro</span>
        </div>
        <Stepper steps={STAFF_STEPS} current={step} />
        {step === 2 && (
          <div style={{ marginTop: 28, padding: '16px 20px', background: 'var(--alpha-white-200)', border: `1px solid ${onPoint(35)}`, borderRadius: 12 }}>
            <p className="text-caption text-[var(--alpha-white-800)]">
              마지막 단계예요.<br />연락처를 입력하고 약관에 동의하면<br />가입 신청이 완료돼요.
            </p>
          </div>
        )}
        <div style={{ marginTop: 'auto', paddingTop: 20 }}>
          <span className="text-caption text-[var(--alpha-white-800)]">v1.0 · 2026</span>
        </div>
      </div>

      {/* 우측 폼 */}
      <div className="bg-surface-raised" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '52px 64px', overflowY: 'auto' }}>
        <div style={{ width: '100%', maxWidth: 480 }}>

          {/* ── Step 1: 사업장 검색 ─────────────────────────────────────── */}
          {step === 1 && (
            <>
              <div className="text-heading-md font-bold text-text" style={{ marginBottom: 8 }}>소속 사업장 검색</div>
              <div className="text-body-sm text-text-secondary" style={{ marginBottom: 28 }}>소속된 사업장의 상호명을 검색해 선택해 주세요.</div>

              {/* 상호명 검색 */}
              <div style={{ marginBottom: 20, position: 'relative' }}>
                <div style={{ position: 'relative' }}>
                  <Input
                    label="사업장 상호명 검색" required width="fill" size="lg"
                    className={selectedBiz ? 'success' : ''}
                    type="text" placeholder="상호명을 입력하세요 (2자 이상)"
                    value={keyword}
                    error={step1Err ?? undefined}
                    onChange={(e) => handleKeywordChange(e.target.value)}
                  />
                  {searching && (
                    <div className="text-caption text-text-secondary" style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)' }}>검색 중…</div>
                  )}
                </div>

                {/* 검색 결과 드롭다운 — 리스트 아이템이라 `.btn` 규격 대상이 아니다(D-09 §15.1은 액션 버튼) */}
                {searchResult.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--shell-surface)', border: '1px solid var(--shell-border)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--elevation-2)', zIndex: 'var(--z-dropdown)', overflow: 'hidden', marginTop: 4 }}>
                    {searchResult.map((item) => (
                      <button
                        key={item.compId}
                        type="button"
                        onClick={() => handleSelectBiz(item)}
                        className="hover:bg-surface"
                        style={{ width: '100%', padding: 'var(--spacing-04) var(--spacing-05)', textAlign: 'left', background: 'none', border: 'none', borderBottom: '1px solid var(--shell-border)', cursor: 'pointer', fontFamily: 'inherit' }}
                      >
                        <div className="text-body-sm font-semibold text-text">{item.compName}</div>
                        <div className="text-caption text-text-secondary" style={{ marginTop: 2 }}>대표자: {item.compRepName} · 사업자: {formatCompNo(item.compNo)}</div>
                      </button>
                    ))}
                  </div>
                )}

                {noResult && !selectedBiz && keyword.trim().length >= 2 && (
                  <p className="text-caption text-text-secondary" style={{ marginTop: 8 }}>검색 결과가 없어요. 상호명을 다시 확인해 주세요.</p>
                )}
              </div>

              {/* 선택된 사업장 카드 */}
              {selectedBiz && (
                <div style={{ padding: 'var(--spacing-05) var(--spacing-06)', background: 'var(--shell-bg)', border: '1px solid var(--color-success)', borderRadius: 'var(--radius-lg)', marginBottom: 20 }}>
                  <div className="text-caption font-bold" style={{ color: 'var(--color-success)', marginBottom: 8 }}>✓ 사업장 선택 완료</div>
                  <div className="text-heading-sm font-bold text-text" style={{ marginBottom: 4 }}>{selectedBiz.compName}</div>
                  <div className="text-caption text-text-secondary">대표자: {selectedBiz.compRepName}</div>
                  <div className="text-caption text-text-secondary">사업자번호: {formatCompNo(selectedBiz.compNo)}</div>
                  <div style={{ marginTop: 8 }}>
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => { setSelectedBiz(null); setKeyword(''); setNoResult(false); }}
                    >
                      다시 선택
                    </Button>
                  </div>
                </div>
              )}

              <div className="flex gap-sm" style={{ marginTop: 4 }}>
                <Button variant="etc" size="lg" onClick={() => void navigate('/signup/id')}>취소</Button>
                <Button variant="primary" size="lg" className="flex-1" onClick={() => { if (validateStep1()) setStep(2); }}>
                  다음 →
                </Button>
              </div>
            </>
          )}

          {/* ── Step 2: 계정 정보 ───────────────────────────────────────── */}
          {step === 2 && (
            <>
              <div className="text-heading-md font-bold text-text" style={{ marginBottom: 8 }}>계정 정보 입력</div>
              <div className="text-body-sm text-text-secondary" style={{ marginBottom: 28 }}>로그인에 사용할 아이디와 비밀번호를 설정해 주세요.</div>

              <div className="flex flex-col gap-md">
                {/* 가입자명 */}
                <Input
                  label="가입자명" required width="fill" size="lg"
                  type="text" placeholder="이름을 입력하세요" value={memberName}
                  error={memberNameErr ?? undefined}
                  onChange={(e) => setMemberName(e.target.value)}
                  onBlur={() => setMemberNameTouched(true)}
                />

                {/*
                  아이디 — 라벨 아래 "입력 + 우측 버튼"이 한 줄인 조합은 ui-core `Input` 계약으로
                  표현할 수 없다(`Input` 은 `.field > label / input / .help` 를 한 덩어리로 렌더해
                  입력칸만 감싸는 지점이 없다). `.field` 표준 클래스로 직접 조립한다 — 색·높이·
                  포커스 링은 그대로 토큰이 구동한다(D-09 §15.2). — PasswordField.tsx 와 동일 사유.
                */}
                <div className={`field field-fill ${(loginIdFormatErr || loginIdState === 'taken') ? 'error' : loginIdState === 'ok' ? 'success' : ''}`}>
                  <label htmlFor={loginIdId}>아이디<span className="req" aria-hidden="true">*</span></label>
                  <div className="flex gap-sm">
                    <input
                      id={loginIdId}
                      className="input input-lg"
                      type="text" placeholder="6자 이상 영문/숫자" value={loginId}
                      onChange={(e) => { setLoginId(e.target.value); setLoginIdState('idle'); setLoginIdFormatErr(null); }}
                      onBlur={() => {
                        if (loginIdState !== 'idle') return;
                        if (!loginId.trim())                setLoginIdFormatErr('아이디를 입력해 주세요.');
                        else if (loginId.trim().length < 6) setLoginIdFormatErr('6자 이상 입력해 주세요.');
                      }}
                    />
                    <Button
                      variant="secondary" size="lg" className="shrink-0 whitespace-nowrap"
                      loading={loginIdState === 'checking'}
                      onClick={() => void handleCheckLoginId()}
                    >
                      {loginIdState === 'checking' ? '확인 중…' : '중복확인'}
                    </Button>
                  </div>
                  {loginIdFormatErr        && <span className="help">{loginIdFormatErr}</span>}
                  {loginIdState === 'taken' && !loginIdFormatErr && <span className="help">이미 사용 중인 아이디예요.</span>}
                  {loginIdState === 'ok'    && <span className="help">사용 가능한 아이디예요.</span>}
                  {!loginIdFormatErr && loginIdState === 'idle' && (
                    <span className="text-caption text-text-secondary">6자 이상, 영문 소문자와 숫자 조합</span>
                  )}
                </div>

                {/* 비밀번호 */}
                <PasswordField
                  label="비밀번호" required size="lg"
                  placeholder="비밀번호 입력" value={password}
                  error={pwErr ?? undefined}
                  hint="8자 이상 · 영문 + 숫자 + 특수문자 포함"
                  footer={<StrengthBar password={password} strength={strength} />}
                  onChange={(e) => setPassword(e.target.value)}
                  onBlur={() => setPwTouched(true)}
                />

                {/* 비밀번호 확인 — 일치 여부는 색만이 아니라 문구로도 알린다(D-07 §3.2 #5) */}
                <PasswordField
                  label="비밀번호 확인" required size="lg"
                  className={pwConfirmOk ? 'success' : ''}
                  placeholder="비밀번호를 다시 입력하세요" value={pwConfirm}
                  error={pwConfirmErr ?? undefined}
                  hint={pwConfirmOk ? '비밀번호가 일치해요.' : undefined}
                  onChange={(e) => setPwConfirm(e.target.value)}
                  onBlur={() => setPwConfirmTouched(true)}
                />

                <div style={{ height: 1, background: 'var(--shell-border)', margin: 'var(--spacing-02) 0' }} />

                {/* 전화번호 + 필수 OTP — 위 아이디와 같은 조립 사유 */}
                <div className={`field field-fill ${mobileNotice ? 'error' : ''}`}>
                  <label htmlFor={mobileId}>전화번호<span className="req" aria-hidden="true">*</span></label>
                  <div className="flex gap-sm">
                    <input
                      id={mobileId}
                      className="input input-lg"
                      type="tel" placeholder="010-0000-0000" value={mobile}
                      onChange={(e) => handleMobileChange(e.target.value)}
                      onBlur={() => void handleMobileBlur()}
                    />
                    {mobileReady && mobileAccountType !== 'general' && !otpSent && !otpVerified && (
                      <PhoneOtpSendButton onClick={() => void handleOtpRequest()} loading={otpLoading} />
                    )}
                  </div>
                  {mobileNotice && <span className="help">{mobileNotice}</span>}
                  {!mobileErr && mobileAccountType === 'sns' && (
                    <span className="text-caption text-text-secondary">SNS로 가입된 번호예요. 계속 진행하면 로그인 정보가 연동돼요.</span>
                  )}
                  {!mobileErr && mobileReady && (
                    <PhoneOtpVerify
                      otpSent={otpSent}
                      otpVerified={otpVerified}
                      onVerify={handleOtpVerify}
                      loading={otpLoading}
                      error={otpError}
                      variant="verify"
                    />
                  )}
                </div>

                {/* 이메일 */}
                <Input
                  label="이메일" required width="fill" size="lg"
                  type="email" placeholder="example@email.com" value={email}
                  error={emailErr ?? undefined}
                  onChange={(e) => { setEmail(e.target.value); setEmailErr(null); }}
                  onBlur={() => void handleEmailBlur()}
                />
              </div>

              <div style={{ marginTop: 20 }}>
                <TermsConsentSection value={terms} onChange={setTerms} />
                {termsErr && <p className="text-caption" style={{ marginTop: -14, marginBottom: 16, color: 'var(--color-error)' }}>{termsErr}</p>}
              </div>

              {submitError && (
                <div style={{ padding: 'var(--spacing-04) var(--spacing-05)', background: 'color-mix(in srgb, var(--color-error) 8%, transparent)', border: '1px solid color-mix(in srgb, var(--color-error) 20%, transparent)', borderRadius: 'var(--radius-md)', marginBottom: 16 }}>
                  <p className="text-body-sm" style={{ color: 'var(--color-error)' }}>{submitError}</p>
                </div>
              )}

              <div className="flex gap-sm">
                <Button variant="etc" size="lg" onClick={() => setStep(1)}>← 이전</Button>
                <Button
                  variant="primary" size="lg" className="flex-1"
                  loading={isLoading}
                  onClick={() => { if (validateStep2()) void handleSubmit(); }}
                >
                  {isLoading ? '처리 중…' : '가입 신청하기'}
                </Button>
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
}
